"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TutorialManifestSchema = exports.TutorialSchema = exports.TutorialVersionSchema = exports.TutorialFileSchema = exports.TUTORIAL_FILE_KINDS = exports.TutorialRenderSchema = exports.TutorialStepSchema = exports.TutorialClipSchema = exports.TutorialAnchorSchema = exports.TUTORIAL_TUTORIAL_STATES = exports.TUTORIAL_RENDER_STATES = exports.TUTORIAL_VERSION_STATUSES = void 0;
const zod_1 = require("zod");
exports.TUTORIAL_VERSION_STATUSES = ['draft', 'published'];
exports.TUTORIAL_RENDER_STATES = ['none', 'queued', 'running', 'ready', 'failed'];
exports.TUTORIAL_TUTORIAL_STATES = ['draft', 'published', 'retired'];
/**
 * How a step finds its element. `elementId` (a data-tutorial-id value) wins when
 * present; otherwise the derived triple route+role+label+nth is used. See spec 3.4.
 */
exports.TutorialAnchorSchema = zod_1.z.object({
    elementId: zod_1.z.string().optional(),
    route: zod_1.z.string(),
    role: zod_1.z.string(),
    label: zod_1.z.string(),
    nth: zod_1.z.number().int().min(0).default(0),
});
/**
 * A clip is either a slice of one continuous session recording, or a standalone
 * re-record of this step alone. Re-recording one step leaves every other step
 * pointing at the original session WebM. See spec 6.1.
 */
exports.TutorialClipSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({
        kind: zod_1.z.literal('slice'),
        sourceKey: zod_1.z.string(),
        startMs: zod_1.z.number().int().min(0),
        endMs: zod_1.z.number().int().min(0),
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('standalone'),
        key: zod_1.z.string(),
        durationMs: zod_1.z.number().int().min(0),
    }),
]);
exports.TutorialStepSchema = zod_1.z.object({
    stepId: zod_1.z.string(),
    order: zod_1.z.number().int().min(0),
    anchor: exports.TutorialAnchorSchema,
    clip: exports.TutorialClipSchema.nullable().default(null),
    /** Mic audio key. Input to STT; kept after publish since 2026-09-29 (marketing source). */
    micKey: zod_1.z.string().nullable().default(null),
    /** What STT heard. Kept so a step can be re-cleaned without re-recording. */
    transcriptRaw: zod_1.z.string().default(''),
    /** Cleaned + author-approved. This is what TTS speaks. */
    narration: zod_1.z.string().default(''),
    narrationApproved: zod_1.z.boolean().default(false),
});
exports.TutorialRenderSchema = zod_1.z.object({
    state: zod_1.z.enum(exports.TUTORIAL_RENDER_STATES).default('none'),
    mp4Key: zod_1.z.string().nullable().default(null),
    posterKey: zod_1.z.string().nullable().default(null),
    vttKey: zod_1.z.string().nullable().default(null),
    durationMs: zod_1.z.number().int().min(0).nullable().default(null),
    error: zod_1.z.string().nullable().default(null),
});
exports.TUTORIAL_FILE_KINDS = ['raw', 'transcript', 'script', 'mp4', 'poster', 'vtt', 'mic'];
exports.TutorialFileSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.TUTORIAL_FILE_KINDS),
    key: zod_1.z.string(),
    sizeBytes: zod_1.z.number().int().min(0),
    createdAt: zod_1.z.number().int(),
    deletedAt: zod_1.z.number().int().optional(),
});
exports.TutorialVersionSchema = zod_1.z.object({
    tutorialId: zod_1.z.string(),
    versionNo: zod_1.z.number().int().min(1),
    status: zod_1.z.enum(exports.TUTORIAL_VERSION_STATUSES),
    steps: zod_1.z.array(exports.TutorialStepSchema).default([]),
    render: exports.TutorialRenderSchema.default({}),
    publishedAt: zod_1.z.string().nullable().default(null),
    publishedBy: zod_1.z.string().nullable().default(null),
    /** 'ai' = TTS narration per step (pre-2026-09-29 behaviour). 'original' = the recorder's own voice. */
    voice: zod_1.z.enum(['ai', 'original']).default('ai'),
    /** Every S3 object this version owns, with its size — drives the Library and the storage meter. */
    files: zod_1.z.array(exports.TutorialFileSchema).default([]),
    /** The marketing source record. Never deleted by publish; only by purge. */
    source: zod_1.z.object({
        rawKey: zod_1.z.string(),
        transcriptKey: zod_1.z.string().nullable().default(null),
        scriptKey: zod_1.z.string().nullable().default(null),
        pages: zod_1.z.array(zod_1.z.object({ atMs: zod_1.z.number().int(), route: zod_1.z.string(), label: zod_1.z.string().default('') })).default([]),
        highlights: zod_1.z.array(zod_1.z.object({ atMs: zod_1.z.number().int() })).default([]),
    }).optional(),
});
exports.TutorialSchema = zod_1.z.object({
    tutorialId: zod_1.z.string(),
    slug: zod_1.z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/, 'slug must be lowercase kebab-case'),
    title: zod_1.z.string(),
    /** Which app the tutorial was recorded in. 'pos' is reserved for sub-project 2. */
    surface: zod_1.z.enum(['platform-app', 'pos']).default('platform-app'),
    state: zod_1.z.enum(exports.TUTORIAL_TUTORIAL_STATES).default('draft'),
    publishedVersionNo: zod_1.z.number().int().min(1).nullable().default(null),
    /**
     * Who owns this tutorial. A merchant's recordings are private to their
     * business; `null` means global — authored by RubyTill staff and visible to
     * every tenant. Nullable with a default so the field is purely additive:
     * rows written before this change parse as global, which is correct, since
     * every tutorial that existed before tenant scoping was super-admin authored.
     */
    tenantId: zod_1.z.string().nullable().default(null),
    createdAt: zod_1.z.number().int(),
    updatedAt: zod_1.z.number().int(),
    /** 'assignment' = recorded for a Video Task; 'tenant' = reserved for tenant-own libraries. Absent = legacy. */
    source: zod_1.z.enum(['assignment', 'tenant']).optional(),
    assignmentId: zod_1.z.string().optional(),
    recordedBy: zod_1.z.object({ tenantId: zod_1.z.string(), userId: zod_1.z.string(), name: zod_1.z.string().default('') }).optional(),
    tags: zod_1.z.array(zod_1.z.string()).default([]),
    adWorthy: zod_1.z.boolean().default(false),
    /** Set by purge. State stays 'retired' — no new state value, so existing readers are unaffected. */
    purgedAt: zod_1.z.number().int().optional(),
    purgedBy: zod_1.z.string().optional(),
});
/** The public JSON on the CDN. Short cache TTL; points at immutable renders. Spec 3.6. */
exports.TutorialManifestSchema = zod_1.z.object({
    slug: zod_1.z.string(),
    state: zod_1.z.enum(exports.TUTORIAL_TUTORIAL_STATES),
    versionNo: zod_1.z.number().int().min(1).nullable().default(null),
    mp4: zod_1.z.string().nullable().default(null),
    poster: zod_1.z.string().nullable().default(null),
    chapters: zod_1.z.string().nullable().default(null),
    durationMs: zod_1.z.number().int().min(0).nullable().default(null),
});
//# sourceMappingURL=tutorials.js.map